package fr.manyverse.app.presentation.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import fr.manyverse.app.domain.model.ImageData

/**
 * State holder for image selection in forms. Bundles the picked [ImageData], a pending
 * removal flag (for editing an entity that already has a remote image) and the launcher,
 * removing the boilerplate otherwise repeated in every image-bearing screen.
 *
 * Obtain one with [rememberImageSelection].
 */
@Stable
class ImageSelection internal constructor() {
    /** The newly picked image, or null if none was picked (or selection was cleared). */
    var selected by mutableStateOf<ImageData?>(null)
        private set

    /** True when the user cleared the image, signalling removal of the existing remote one. */
    var isRemovalRequested by mutableStateOf(false)
        private set

    internal var launch: () -> Unit = {}

    /** Opens the platform image picker. */
    fun pick() = launch()

    /** Clears the selection and marks any existing remote image for removal. */
    fun clear() {
        selected = null
        isRemovalRequested = true
    }

    /** Resets to the initial state (e.g. when a dialog is dismissed or reopened). */
    fun reset() {
        selected = null
        isRemovalRequested = false
    }

    /** Resolves which existing image URL to display, honoring a pending removal. */
    fun displayUrl(currentUrl: String?): String? = if (isRemovalRequested) null else currentUrl

    internal var onPickedListener: (ImageData) -> Unit = {}

    internal fun onPicked(data: ImageData) {
        selected = data
        isRemovalRequested = false
        onPickedListener(data)
    }
}

/** @param onPicked appelé après chaque nouvelle image choisie (ex. pré-remplir un nom). */
@Composable
fun rememberImageSelection(onPicked: (ImageData) -> Unit = {}): ImageSelection {
    val selection = remember { ImageSelection() }
    val launcher = rememberImagePickerLauncher { selection.onPicked(it) }
    SideEffect {
        selection.launch = launcher
        selection.onPickedListener = onPicked
    }
    return selection
}
